import { useState, useCallback, useEffect, type RefObject } from 'react';

interface CropInteractionOptions {
  maxScale: number;
  initialScale: number;
  canvasSize: number; // For square canvases (PFP mode)
  canvasWidth?: number; // For rectangular canvases (banner mode)
  canvasHeight?: number; // For rectangular canvases (banner mode)
  displayScale?: number; // Scale factor from viewport for precise mapping
  horizontalOnly?: boolean;
  verticalOnly?: boolean;
  snapToCenter?: boolean; // Snap to canvas center (like meme component)
  snapThreshold?: number; // Pixels from center to trigger snap
  disableWheelZoom?: boolean; // Disable mouse wheel zoom (use slider only)
  disabled?: boolean; // Disable all interactions (dragging)
}

export interface SnapGuides {
  horizontal: boolean; // Snapped to horizontal center (shows vertical line)
  vertical: boolean;   // Snapped to vertical center (shows horizontal line)
}

interface ImageBounds {
  width: number;
  height: number;
}

export function useCropInteraction(
  containerRef: RefObject<HTMLDivElement>,
  options: CropInteractionOptions
) {
  const { 
    maxScale, 
    initialScale, 
    canvasSize, 
    canvasWidth, 
    canvasHeight, 
    displayScale = 1,
    horizontalOnly = false, 
    verticalOnly = false, 
    snapToCenter = false, 
    snapThreshold = 12, 
    disableWheelZoom = false, 
    disabled = false 
  } = options;

  // Use explicit width/height if provided, otherwise use canvasSize for both
  const effectiveWidth = canvasWidth ?? canvasSize;
  const effectiveHeight = canvasHeight ?? canvasSize;

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScaleState] = useState(initialScale);
  const [minScale, setMinScale] = useState(1);
  const [_imageBounds, setImageBoundsState] = useState<ImageBounds | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [offsetStart, setOffsetStart] = useState({ x: 0, y: 0 });
  const [snapGuides, setSnapGuides] = useState<SnapGuides>({ horizontal: false, vertical: false });

  // Minimum scale is always 1.0 - no zoom out below 100%
  // This prevents the nub/character from becoming smaller than the canvas
  useEffect(() => {
    setMinScale(1);
  }, []);

  // Set scale with bounds checking
  const setScale = useCallback(
    (newScale: number) => {
      const clampedScale = Math.max(minScale, Math.min(maxScale, newScale));
      setScaleState(clampedScale);
    },
    [minScale, maxScale]
  );

  // Set image bounds for min scale calculation
  const setImageBounds = useCallback((bounds: ImageBounds) => {
    setImageBoundsState(bounds);
  }, []);

  // Reset to initial state
  const reset = useCallback(() => {
    setOffset({ x: 0, y: 0 });
    setScaleState(initialScale);
  }, [initialScale]);

  // Get snapped position (snap to center when close enough)
  const getSnappedPosition = useCallback(
    (rawX: number, rawY: number): { x: number; y: number; snappedH: boolean; snappedV: boolean } => {
      // Center is at offset 0,0
      let snappedX = rawX;
      let snappedY = rawY;
      let snappedH = false;
      let snappedV = false;

      if (snapToCenter) {
        // Snap to horizontal center (element X near center)
        if (Math.abs(rawX) < snapThreshold) {
          snappedX = 0;
          snappedH = true;
        }

        // Snap to vertical center (element Y near center)
        if (Math.abs(rawY) < snapThreshold) {
          snappedY = 0;
          snappedV = true;
        }
      }

      return { x: snappedX, y: snappedY, snappedH, snappedV };
    },
    [snapToCenter, snapThreshold]
  );

  // Clamp offset to allow movement within reasonable bounds
  const clampOffset = useCallback(
    (newOffset: { x: number; y: number }, currentScale: number, updateGuides: boolean = false) => {
      // Calculate separate limits for X and Y based on canvas dimensions
      const scaledWidth = effectiveWidth * currentScale;
      const scaledHeight = effectiveHeight * currentScale;

      // Allow movement based on overflow when zoomed in,
      // or allow free movement within canvas bounds when at 100% or less
      const overflowX = (scaledWidth - effectiveWidth) / 2;
      const overflowY = (scaledHeight - effectiveHeight) / 2;

      // Allow character to move across the full canvas width/height
      const minAllowedX = effectiveWidth * 0.75; // Allow 75% canvas width movement
      const minAllowedY = effectiveHeight * 0.75; // Allow 75% canvas height movement

      const maxOffsetX = Math.max(overflowX, minAllowedX);
      const maxOffsetY = Math.max(overflowY, minAllowedY);

      // Apply center snapping
      const { x: snappedX, y: snappedY, snappedH, snappedV } = getSnappedPosition(newOffset.x, newOffset.y);

      if (updateGuides) {
        setSnapGuides({ horizontal: snappedH, vertical: snappedV });
      }

      return {
        x: Math.max(-maxOffsetX, Math.min(maxOffsetX, snappedX)),
        y: Math.max(-maxOffsetY, Math.min(maxOffsetY, snappedY)),
      };
    },
    [effectiveWidth, effectiveHeight, getSnappedPosition]
  );

  // Handle mouse down for drag start
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (disabled) return; // Don't start drag if disabled
      if (e.button !== 0) return; // Only left click
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setOffsetStart(offset);
    },
    [offset, disabled]
  );

  // Handle mouse move for dragging
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      // Map screen delta to internal coordinate delta using displayScale
      const deltaX = verticalOnly ? 0 : (e.clientX - dragStart.x) / displayScale;
      const deltaY = horizontalOnly ? 0 : (e.clientY - dragStart.y) / displayScale;

      const newOffset = {
        x: verticalOnly ? offsetStart.x : offsetStart.x + deltaX,
        y: horizontalOnly ? offsetStart.y : offsetStart.y + deltaY,
      };

      setOffset(clampOffset(newOffset, scale, true));
    },
    [isDragging, dragStart, offsetStart, scale, clampOffset, horizontalOnly, verticalOnly, displayScale]
  );

  // Handle mouse up for drag end
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setSnapGuides({ horizontal: false, vertical: false });
  }, []);

  // Handle wheel for zooming
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent modal/parent scroll

      const zoomFactor = 0.1;
      const delta = e.deltaY > 0 ? -zoomFactor : zoomFactor;
      const newScale = Math.max(minScale, Math.min(maxScale, scale + delta));

      setScaleState(newScale);

      // Adjust offset when zooming to keep content centered
      const newOffset = clampOffset(offset, newScale);
      setOffset(newOffset);
    },
    [scale, minScale, maxScale, offset, clampOffset]
  );

  // Handle touch events for mobile
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (disabled) return; // Don't start drag if disabled
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: touch.clientX, y: touch.clientY });
        setOffsetStart(offset);
      }
    },
    [offset, disabled]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;

      // Prevent scrolling while dragging
      e.preventDefault();

      const touch = e.touches[0];
      // Map screen delta to internal coordinate delta using displayScale
      const deltaX = verticalOnly ? 0 : (touch.clientX - dragStart.x) / displayScale;
      const deltaY = horizontalOnly ? 0 : (touch.clientY - dragStart.y) / displayScale;

      const newOffset = {
        x: verticalOnly ? offsetStart.x : offsetStart.x + deltaX,
        y: horizontalOnly ? offsetStart.y : offsetStart.y + deltaY,
      };

      setOffset(clampOffset(newOffset, scale, true));
    },
    [isDragging, dragStart, offsetStart, scale, clampOffset, horizontalOnly, verticalOnly, displayScale]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setSnapGuides({ horizontal: false, vertical: false });
  }, []);

  const handleTouchCancel = useCallback(() => {
    setIsDragging(false);
    setSnapGuides({ horizontal: false, vertical: false });
  }, []);

  // Attach event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mousedown', handleMouseDown);
    if (!disableWheelZoom) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchCancel);

    // Global listeners for mouse up/move (to handle drag outside container)
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      if (!disableWheelZoom) {
        container.removeEventListener('wheel', handleWheel);
      }
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    containerRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    disableWheelZoom,
  ]);

  return {
    offset,
    scale,
    minScale,
    setScale,
    setImageBounds,
    reset,
    isDragging,
    snapGuides,
  };
}
